import { baseFileName } from "./file-name-core.js";

export const PDF_TO_TEXT_LIMITS = Object.freeze({
  maxBytes: 50 * 1024 * 1024,
  maxPages: 100,
  maxOcrPages: 20,
  searchableCharacters: 20,
  outputCharacters: 8_000_000
});

export function pdfTextFileName(fileName) {
  return `${baseFileName(fileName || "document.pdf")}-text.txt`;
}

export function hasMeaningfulPdfText(items, minimumCharacters = PDF_TO_TEXT_LIMITS.searchableCharacters) {
  const text = Array.isArray(items)
    ? items.map(item => typeof item === "string" ? item : item?.str || "").join("")
    : String(items || "");
  return text.replace(/\s+/gu, "").length >= minimumCharacters;
}

export function assertAutomaticOcrPageLimit(pageCount, limit = PDF_TO_TEXT_LIMITS.maxOcrPages) {
  const count = Math.max(0, Number(pageCount) || 0);
  if (count <= limit) return;
  const error = new Error(
    `This PDF contains ${count} pages that need OCR. Automatic OCR supports up to ${limit}. Disable automatic OCR to extract existing text, or split the PDF first.`
  );
  error.code = "PDF_TEXT_OCR_PAGE_LIMIT";
  throw error;
}

export function extractPositionedPageText(items) {
  const runs = (items || []).map((item, index) => {
    const text = String(item?.str || "").replace(/\s+/gu, " ").trim();
    const transform = Array.isArray(item?.transform) ? item.transform : [];
    const x = Number(transform[4]);
    const y = Number(transform[5]);
    const height = Math.max(1, Math.abs(Number(item?.height)) || Math.hypot(Number(transform[2]) || 0, Number(transform[3]) || 0) || 10);
    return text && Number.isFinite(x) && Number.isFinite(y)
      ? { text, x, y, height, direction: item?.dir === "rtl" ? "rtl" : "ltr", hasEOL: item?.hasEOL === true, index }
      : null;
  }).filter(Boolean);

  if (!runs.length) return "";
  const lines = [];
  for (const run of runs.sort((left, right) => right.y - left.y || left.x - right.x || left.index - right.index)) {
    const tolerance = Math.max(2, run.height * .5);
    let line = lines.find(candidate => Math.abs(candidate.y - run.y) <= Math.max(tolerance, candidate.height * .5));
    if (!line) {
      line = { y: run.y, height: run.height, runs: [] };
      lines.push(line);
    }
    line.runs.push(run);
    line.y = (line.y * (line.runs.length - 1) + run.y) / line.runs.length;
    line.height = Math.max(line.height, run.height);
  }

  return lines
    .sort((left, right) => right.y - left.y)
    .map(line => {
      const rtl = line.runs.filter(run => run.direction === "rtl").length > line.runs.length / 2;
      return joinTextRuns(line.runs.sort((left, right) => rtl ? right.x - left.x : left.x - right.x));
    })
    .filter(Boolean)
    .join("\n");
}

export function extractOcrPageText(lines) {
  return (lines || [])
    .filter(line => String(line?.text || "").trim())
    .sort((left, right) => Number(left.y) - Number(right.y) || (
      left.direction === "rtl" && right.direction === "rtl"
        ? Number(right.x) - Number(left.x)
        : Number(left.x) - Number(right.x)
    ))
    .map(line => String(line.text).replace(/\s+/gu, " ").trim())
    .join("\n");
}

export function choosePageText(selectableText, ocrText) {
  const recognized = normalizePageText(ocrText);
  return recognized || normalizePageText(selectableText);
}

export function formatTextPages(pages, {
  includeLabels = true,
  maxCharacters = PDF_TO_TEXT_LIMITS.outputCharacters
} = {}) {
  const sections = (pages || []).map((page, index) => {
    const pageNumber = Number(page?.pageNumber) || index + 1;
    const text = normalizePageText(page?.text);
    return includeLabels ? `--- Page ${pageNumber} ---${text ? `\n${text}` : ""}` : text;
  });
  const output = sections.join("\n\n");
  if (output.length > maxCharacters) {
    const error = new Error(`The extracted text is longer than the ${maxCharacters.toLocaleString("en-US")} character limit.`);
    error.code = "PDF_TEXT_TOO_LONG";
    throw error;
  }
  return output;
}

export function countTextContent(value) {
  const text = String(value || "");
  return {
    characters: text.length,
    lines: text ? text.split("\n").length : 0,
    contentCharacters: text.replace(/--- Page \d+ ---/gu, "").replace(/\s+/gu, "").length
  };
}

function joinTextRuns(runs) {
  let output = "";
  for (const run of runs) {
    if (!output) {
      output = run.text;
      continue;
    }
    const needsSpace = !/\s$/u.test(output)
      && !/^\s/u.test(run.text)
      && !/^[,.;:!?%)\]}\u3001\u3002]/u.test(run.text)
      && !/[(\[{/\-]$/u.test(output);
    output += `${needsSpace ? " " : ""}${run.text}`;
  }
  return output.trim();
}

function normalizePageText(value) {
  return String(value || "")
    .replace(/\r\n?/gu, "\n")
    .split("\n")
    .map(line => line.replace(/[^\S\n]+/gu, " ").trimEnd())
    .join("\n")
    .replace(/\n{3,}/gu, "\n\n")
    .trim();
}
